// Zustand store pro workspace join code — ověření při prvním spuštění + při každém startu.
//
// Flow:
//   1. init() — načte uložený kód z Preferences, ověří ho na serveru.
//      Pokud kód změnil admin → vyhodí na WorkspaceJoinScreen.
//   2. join(code) — POST /mobile/workspace/join, při úspěchu uloží kód + workspace_id.
//   3. clear() — odhlášení z workspace (smaže kód + token).

import { create } from "zustand";
import { Preferences } from "@capacitor/preferences";

const CODE_KEY = "routera.workspace.code";
const WS_KEY = "routera.workspace.id";

const API_URL = (import.meta as any).env?.VITE_API_URL ?? "http://localhost:8765";

interface WorkspaceState {
  workspaceId: string | null;
  initialized: boolean;
  error: string | null;
  init: () => Promise<void>;
  join: (code: string) => Promise<void>;
  clear: () => Promise<void>;
}

async function _verifyCode(code: string): Promise<string> {
  const res = await fetch(`${API_URL}/mobile/workspace/join`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) {
    const body: any = await res.json().catch(() => ({}));
    throw new Error(body?.detail ?? "Nesprávný kód pracoviště");
  }
  const data: any = await res.json();
  return String(data.workspace_id);
}

export const useWorkspaceStore = create<WorkspaceState>((set) => ({
  workspaceId: null,
  initialized: false,
  error: null,

  init: async () => {
    const { value: storedCode } = await Preferences.get({ key: CODE_KEY });
    if (!storedCode) {
      set({ initialized: true });
      return;
    }
    try {
      const workspaceId = await _verifyCode(storedCode);
      await Preferences.set({ key: WS_KEY, value: workspaceId });
      set({ workspaceId, initialized: true });
    } catch {
      // Kód se změnil nebo server nedostupný — vyhodit na join screen
      await Preferences.remove({ key: CODE_KEY });
      await Preferences.remove({ key: WS_KEY });
      set({ workspaceId: null, initialized: true });
    }
  },

  join: async (code: string) => {
    set({ error: null });
    const workspaceId = await _verifyCode(code);
    await Preferences.set({ key: CODE_KEY, value: code });
    await Preferences.set({ key: WS_KEY, value: workspaceId });
    set({ workspaceId });
  },

  clear: async () => {
    await Preferences.remove({ key: CODE_KEY });
    await Preferences.remove({ key: WS_KEY });
    set({ workspaceId: null });
  },
}));
